import React, { useState, useEffect, useRef, useId } from "react";
import { ClickAwayListener, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse, parseISO, isValid } from "date-fns";
import {
  CalendarPopper,
  DateTextField,
  ErrorMessage,
  IconWrapper,
  InputDateWrapper,
  InputFieldWrapper,
  StyledInput,
} from "./styles";
import { IconButton } from "../../IconButton";
import { DateInputFieldProps } from "./types";

const DATE_FORMAT = "yyyy/MM/dd";

const formatDate = (date: Date | string | null): string => {
  if (!date) return "";
  try {
    return format(date, DATE_FORMAT);
  } catch {
    return "";
  }
};

// Forms hold ISO strings, which DateCalendar treats as invalid and opens on today.
const toDate = (value: Date | string | null): Date | null => {
  if (!value) return null;
  const date = typeof value === "string" ? parseISO(value) : value;
  return isValid(date) ? date : null;
};

const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  const parsedDate = parse(dateString, DATE_FORMAT, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

const maskDateInput = (value: string) => {
  let digits = value.replaceAll(/\D/g, "");
  let year = digits.substring(0, 4);
  let month = digits.substring(4, 6);
  let day = digits.substring(6, 8);

  if (day.length === 1 && Number.parseInt(day, 10) > 3) day = "0" + day;
  if (day.length === 2) {
    const dNum = Number.parseInt(day, 10);
    if (dNum < 1 || dNum > 31) day = "31";
  }

  if (month.length === 1 && Number.parseInt(month, 10) > 1) month = "0" + month;
  if (month.length === 2) {
    const mNum = Number.parseInt(month, 10);
    if (mNum < 1 || mNum > 12) month = "12";
  }

  let result = year;
  if (month) result += "/" + month;
  if (day) result += "/" + day;
  return result;
};

export const DateInputField: React.FC<DateInputFieldProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  error,
  minDate,
  maxDate,
  handleClear = false,
  testId,
  autoComplete,
  label,
  id,
}) => {
  const date = toDate(value);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatDate(date));
  const anchorRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const inputId = id ?? testId ?? generatedId;
  const errorId = error ? `${inputId}-error` : undefined;

  // keep local input in sync with external changes
  useEffect(() => {
    setInputValue(formatDate(toDate(value)));
  }, [value]);

  const toggleCalendar = () => {
    if (!disabled) setOpen((prev) => !prev);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(maskDateInput(e.target.value));
  };

  const handleInputBlur = () => {
    const newDate = parseDate(inputValue);
    if (newDate) {
      onChange(newDate);
      setInputValue(formatDate(newDate));
    } else {
      setInputValue(formatDate(date));
    }
  };

  const handleClearClick = () => {
    onChange(null);
    setInputValue("");
  };

  const showClear = !disabled && handleClear && !!value;

  return (
    <InputDateWrapper ref={anchorRef}>
      {label ? (
        <DateTextField
          fullWidth
          label={label}
          variant="outlined"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          error={!!error}
          helperText={error}
          autoComplete={autoComplete}
          slotProps={{
            htmlInput: {
              "data-testid": testId,
              maxLength: 10,
              "aria-invalid": !!error,
            },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {showClear && (
                    <IconButton
                      onClick={handleClearClick}
                      disabled={disabled}
                      aria-label="Clear date"
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                  <IconButton
                    data-testid={`${testId}-calendar-icon`}
                    onClick={toggleCalendar}
                    disabled={disabled}
                    aria-label="Open calendar"
                  >
                    <CalendarTodayIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ) : (
        <InputFieldWrapper>
          <StyledInput
            id={inputId}
            data-testid={testId}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            $error={!!error}
            maxLength={10}
            autoComplete={autoComplete}
            aria-invalid={!!error}
            aria-describedby={errorId}
          />

          <IconWrapper $error={!!error} $position="calendar">
            <IconButton
              size="small"
              data-testid={`${testId}-calendar-icon`}
              onClick={toggleCalendar}
              disabled={disabled}
              aria-label="Open calendar"
            >
              <CalendarTodayIcon />
            </IconButton>
          </IconWrapper>

          {showClear && (
            <IconWrapper $error={!!error} $position="clear">
              <IconButton
                size="small"
                onClick={handleClearClick}
                aria-label="Clear date"
              >
                <ClearIcon />
              </IconButton>
            </IconWrapper>
          )}
        </InputFieldWrapper>
      )}

      <CalendarPopper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              fallbackPlacements: [
                "top-end",
                "top-start",
                "bottom-end",
                "left",
                "right",
              ],
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "offset",
            enabled: true,
            options: {
              offset: [-75, 0],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={date}
                onChange={(newValue, selectionState) => {
                  onChange(newValue);
                  setInputValue(formatDate(newValue));
                  if (selectionState === "finish") setOpen(false);
                }}
                minDate={minDate}
                maxDate={maxDate}
                disabled={disabled}
              />
            </LocalizationProvider>
          </div>
        </ClickAwayListener>
      </CalendarPopper>

      {!label && error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </InputDateWrapper>
  );
};
